/**
 * Supported UI/content languages. `code` is the locale stored on the user and
 * sent to the API; `label` is the native name shown in the picker; `rtl` flags
 * right-to-left scripts (Arabic) so the app can flip direction.
 *
 * Keep this list in sync with the admin's authorable locales
 * (BaseAdminController::LOCALES) and the catalog translation tables.
 */
export interface LocaleDef {
  code: string;
  label: string;
  rtl: boolean;
}

export const SUPPORTED: LocaleDef[] = [
  { code: 'en', label: 'English',    rtl: false },
  { code: 'de', label: 'Deutsch',    rtl: false },
  { code: 'es', label: 'Español',    rtl: false },
  { code: 'fr', label: 'Français',   rtl: false },
  { code: 'it', label: 'Italiano',   rtl: false },
  { code: 'pt', label: 'Português',  rtl: false },
  { code: 'uk', label: 'Українська', rtl: false },
  { code: 'ar', label: 'العربية',    rtl: true  },
];

export const DEFAULT_LOCALE = 'en';

const SUPPORTED_CODES = new Set(SUPPORTED.map((l) => l.code));

export function isSupported(code: string | null | undefined): code is string {
  return code != null && SUPPORTED_CODES.has(code);
}

export function localeDef(code: string): LocaleDef {
  return SUPPORTED.find((l) => l.code === code) ?? SUPPORTED[0];
}

export function isRtl(code: string): boolean {
  return localeDef(code).rtl;
}

/**
 * Best supported match for the browser's preferred languages. Matches on the
 * primary subtag (e.g. "de-AT" → "de"), falling back to English.
 */
export function detectBrowserLocale(): string {
  const candidates =
    typeof navigator !== 'undefined'
      ? [navigator.language, ...(navigator.languages ?? [])]
      : [];
  for (const tag of candidates) {
    if (!tag) continue;
    const primary = tag.toLowerCase().split('-')[0];
    if (SUPPORTED_CODES.has(primary)) return primary;
  }
  return DEFAULT_LOCALE;
}
